import type { Genome, EvolutionTrait } from '@/domain/genetics/types';
import type { DeathCause, LineageGraph, LineageNode } from './types';
import { LineageCycleError } from './types';
import { findAncestors } from './queries';

export function emptyGraph(): LineageGraph {
  return { nodes: {}, rootId: null };
}

export interface RecordBirthInput {
  id: string;
  name: string;
  genome: Genome;
  traits: EvolutionTrait[];
  bornAt: number;
  parentIds: [string | null, string | null];
}

export function recordBirth(g: LineageGraph, input: RecordBirthInput): LineageGraph {
  // Garante acíclico: se algum dos pais informados é descendente do filho, recusar.
  for (const pid of input.parentIds) {
    if (pid === null) continue;
    if (pid === input.id) throw new LineageCycleError(input.id, pid);
    // Se input.id já existe e tem descendentes que incluem pid, é ciclo.
    if (g.nodes[input.id]) {
      const ancestors = findAncestors(g, pid);
      if (ancestors.some((n) => n.creatureId === input.id)) {
        throw new LineageCycleError(input.id, pid);
      }
    }
  }
  const node: LineageNode = {
    creatureId: input.id,
    name: input.name,
    bornAt: input.bornAt,
    diedAt: null,
    cause: 'alive',
    parentIds: input.parentIds,
    childrenIds: [],
    genomeSnapshot: input.genome,
    traitsSnapshot: input.traits,
  };
  const nodes: Record<string, LineageNode> = { ...g.nodes, [input.id]: node };
  // Atualiza pais com este filho
  for (const pid of input.parentIds) {
    if (pid === null) continue;
    const parent = nodes[pid];
    if (parent && !parent.childrenIds.includes(input.id)) {
      nodes[pid] = { ...parent, childrenIds: [...parent.childrenIds, input.id] };
    }
  }
  const rootId = g.rootId ?? input.id;
  return { nodes, rootId };
}

export function recordDeath(g: LineageGraph, id: string, cause: DeathCause, atMs: number): LineageGraph {
  const node = g.nodes[id];
  if (!node) return g;
  if (node.diedAt !== null) return g; // idempotente
  return {
    ...g,
    nodes: {
      ...g.nodes,
      [id]: { ...node, diedAt: atMs, cause },
    },
  };
}
